import type { Editor, EditorEvent, Events as TinyEvents } from 'hugerte';

import { isElementWrappedInCloser, isVideoElement, wrapElementInCloser } from './wrapElementInCloser';

// Restructure the editor's live DOM so the wrapper sits in its own `<p>`,
// matching what the saved HTML will look like after htmlparser2 auto-closes
// the surrounding paragraph around the block-level wrapper. Only applies when
// the wrapper landed in a `<p>` or at the editor root — in a `<li>`, `<td>`,
// `<blockquote>`, etc. we leave it in place so we don't break the surrounding
// block's semantics (e.g. an image dropped into a list item shouldn't detach
// the rest of the bullet).
function restructureForParagraphFlow(editor: Editor, wrappedElement: Element): HTMLElement | null {
	const root = editor.dom.getRoot();
	const parent = wrappedElement.parentElement;
	const inParagraph = parent?.tagName === 'P';
	const atRoot = !parent || parent === root;
	if (!inParagraph && !atRoot) return null;
	if (inParagraph) {
		// `editor.dom.split` lifts the wrapper out of its enclosing paragraph
		// and drops empty result halves automatically.
		editor.dom.split(parent, wrappedElement);
	}
	const wrapperP = editor.dom.create('p');
	const grandparent = wrappedElement.parentNode ?? root;
	grandparent.insertBefore(wrapperP, wrappedElement);
	wrapperP.appendChild(wrappedElement);
	return wrapperP;
}

// Land the caret at the start of the paragraph following the wrapper (so the
// user can keep typing where their text continues); otherwise park the caret
// right after the wrapper within its parent.
function placeCaretAfterMedia(editor: Editor, anchor: Element): void {
	const anchorParent = anchor.parentNode ?? editor.dom.getRoot();
	const afterP = anchor.nextElementSibling;
	if (afterP?.tagName === 'P') {
		editor.selection.setCursorLocation(afterP, 0);
		return;
	}
	const anchorIndex = Array.from(anchorParent.childNodes).indexOf(anchor);
	editor.selection.setCursorLocation(anchorParent, anchorIndex + 1);
}

export const onNodeChangeCallback = ({ element, target }: EditorEvent<TinyEvents.NodeChangeEvent>): void => {
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const editor = target as Editor;
	const images = element.tagName === 'IMG' ? [element] : Array.from(element.getElementsByTagName('IMG'));
	const iframes = element.tagName === 'IFRAME' ? [element] : Array.from(element.getElementsByTagName('IFRAME'));

	[...images, ...iframes].forEach((mediaElement) => {
		if (isElementWrappedInCloser(mediaElement)) return;
		const wrappedElement = wrapElementInCloser(mediaElement);
		mediaElement.replaceWith(wrappedElement);
		const wrapperP = restructureForParagraphFlow(editor, wrappedElement);
		if (editor.hasFocus()) {
			placeCaretAfterMedia(editor, wrapperP ?? wrappedElement);
		}
	});
	if (isVideoElement(element)) {
		editor.selection.collapse(false);
	}
};
